import React, { useCallback, useEffect, useState } from 'react'
import { apiUrls } from '@http/api-urls'
import { httpService } from '@http/service'
// import * as Sentry from '@sentry/react'
import { Timer } from '@shared/components/timer'
import { createAsyncHook } from '@shared/hooks/use-async-hook'
import { formatPhone } from '@shared/utils/helpers'

import { SignConfigApiModel, SignVerifyResultApiModel } from '../../../api-types'

import { OwnProps } from './model'
import { View } from './view'

const createTimestampOffset = (seconds: number) => new Date().getDate() + seconds * 1000

const useCodeConfig = createAsyncHook({
	call: () => httpService.post<SignConfigApiModel>(apiUrls.esia.getSignConfig()),
	onError: (errText, err) => {
		alert(errText)
		// Sentry.captureException(err)
	},
})

const useVerifyCode = createAsyncHook({
	call: async (code: string): Promise<SignVerifyResultApiModel> => {
		const result = await httpService.post<SignVerifyResultApiModel>(
			apiUrls.esia.verifySMSCode(code)
		)
		if (result.verified) {
			return result
		}

		throw new Error('Не верный код')
	},
})

export const BrokerSignSMSCodeForm = (ownProps: OwnProps) => {
	const codeConfig = useCodeConfig()
	const verifyCode = useVerifyCode()
	const [code, setCode] = useState('')
	const [resendTimestamp, setResendTimestamp] = useState<number | null>(null)
	const [isResendDisabled, setResendDisabled] = useState(true)

	useEffect(() => {
		codeConfig.runRequest()
	}, [])

	useEffect(() => {
		if (codeConfig.data) {
			setResendTimestamp(createTimestampOffset(codeConfig.data.durationBetweenCodeRequests))
		}
	}, [codeConfig.data, setResendTimestamp])

	useEffect(() => {
		if (verifyCode.errorText) {
			setCode('')
		}
	}, [verifyCode.errorText, setCode, codeConfig.data])

	useEffect(() => {
		if (codeConfig.data) {
			verifyCode.clearError()
			setCode('')
		}
	}, [codeConfig.data, verifyCode.clearError, setCode])

	const sendCode = async (value: string) => {
		const verifyResult = await verifyCode.runRequest(value)
		if (verifyResult?.verified) {
			ownProps.onNext()
		}
	}

	const handleChangeCode = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			verifyCode.clearError()
			const value = e.target.value
			setCode(value)
			if (value.length === codeConfig.data?.codeLength) {
				sendCode(value)
			}
		},
		[codeConfig.data, setCode, verifyCode.clearError]
	)

	const handleResendCode = useCallback(async () => {
		setResendDisabled(true)
		await codeConfig.runRequest()
	}, [setResendTimestamp, codeConfig.runRequest, setResendDisabled, setCode])

	const clearResendDisabled = useCallback(() => {
		setResendDisabled(false)
	}, [setResendDisabled])

	return View({
		phone: formatPhone(ownProps.user.phone),
		isResendDisabled,
		codeLength: codeConfig.data?.codeLength || 0,
		isLoading: verifyCode.isLoading,
		isConfigLoading: codeConfig.isLoading,
		code,
		errorText: verifyCode.errorText,
		title: ownProps.title,
		onChangeCode: handleChangeCode,
		onResendCode: handleResendCode,
		timer: <Timer timestamp={resendTimestamp!} onTimerEnd={clearResendDisabled} />,
	})
}
